import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useShare } from './ShareContext';
import { communityService } from '../services/communityService';
import supabase from '../supabase/supabase';

const CommunityContext = createContext(null);

export const CommunityProvider = ({ children }) => {
  const { user } = useShare();
  const [favorites, setFavorites] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchSubscriptions, setSearchSubscriptions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [preferences, setPreferences] = useState(communityService.defaultPreferences);
  const [blocks, setBlocks] = useState([]);
  const [trustCache, setTrustCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [featureAvailable, setFeatureAvailable] = useState(true);

  const refreshCommunity = useCallback(async () => {
    if (!user?.id) {
      setFavorites([]);
      setSubscriptions([]);
      setSearchSubscriptions([]);
      setNotifications([]);
      setBlocks([]);
      setPreferences(communityService.defaultPreferences);
      return;
    }

    setLoading(true);
    try {
      const dashboard = await communityService.getDashboard(user.id);
      setFavorites(dashboard.favorites);
      setSubscriptions(dashboard.subscriptions);
      setSearchSubscriptions(dashboard.searchSubscriptions || []);
      setNotifications(dashboard.notifications);
      setPreferences(dashboard.preferences);
      setBlocks(dashboard.blocks);
      setFeatureAvailable(true);

      supabase.functions.invoke('dispatch-push', {
        body: { reason: 'active-session' },
      }).catch((error) => {
        console.warn('Ausstehende Push-Nachrichten konnten nicht versendet werden:', error);
      });
    } catch (error) {
      console.error('Community-Funktionen konnten nicht geladen werden:', error);
      setFeatureAvailable(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshCommunity();
  }, [refreshCommunity]);

  const favoriteKeys = useMemo(() => new Set(
    favorites.map((favorite) => `${favorite.target_type}:${favorite.target_id}`),
  ), [favorites]);

  const subscriptionIds = useMemo(() => new Set(
    subscriptions.map((subscription) => String(subscription.provider_id)),
  ), [subscriptions]);

  const blockedIds = useMemo(() => new Set(
    blocks.map((block) => String(block.blocked_id)),
  ), [blocks]);

  const toggleFavorite = useCallback(async (targetType, targetId) => {
    if (!user?.id) throw new Error('Bitte melde dich an, um Angebote zu speichern.');
    const key = `${targetType}:${targetId}`;
    const enabled = !favoriteKeys.has(key);
    await communityService.setFavorite(user.id, targetType, targetId, enabled);
    await refreshCommunity();
    return enabled;
  }, [user?.id, favoriteKeys, refreshCommunity]);

  const toggleProviderSubscription = useCallback(async (providerId) => {
    if (!user?.id) throw new Error('Bitte melde dich an, um Anbieter zu abonnieren.');
    const enabled = !subscriptionIds.has(String(providerId));
    await communityService.setProviderSubscription(user.id, providerId, enabled);
    await refreshCommunity();
    return enabled;
  }, [user?.id, subscriptionIds, refreshCommunity]);

  const savePreferences = useCallback(async (nextPreferences) => {
    if (!user?.id) throw new Error('Bitte melde dich an.');
    const saved = await communityService.savePreferences(user.id, nextPreferences);
    setPreferences(saved);
    return saved;
  }, [user?.id]);

  const createSearchSubscription = useCallback(async (subscription) => {
    if (!user?.id) throw new Error('Bitte melde dich an.');
    const saved = await communityService.createSearchSubscription(user.id, subscription);
    setSearchSubscriptions((items) => [saved, ...items]);
    return saved;
  }, [user?.id]);

  const updateSearchSubscription = useCallback(async (subscriptionId, updates) => {
    if (!user?.id) throw new Error('Bitte melde dich an.');
    const saved = await communityService.updateSearchSubscription(user.id, subscriptionId, updates);
    setSearchSubscriptions((items) => items.map((item) => item.id === subscriptionId ? saved : item));
    return saved;
  }, [user?.id]);

  const deleteSearchSubscription = useCallback(async (subscriptionId) => {
    if (!user?.id) throw new Error('Bitte melde dich an.');
    await communityService.deleteSearchSubscription(user.id, subscriptionId);
    setSearchSubscriptions((items) => items.filter((item) => item.id !== subscriptionId));
  }, [user?.id]);

  const markRead = useCallback(async (notificationId) => {
    if (!user?.id) return;
    await communityService.markNotificationRead(user.id, notificationId);
    setNotifications((items) => items.map((item) => (
      item.id === notificationId ? { ...item, read_at: new Date().toISOString() } : item
    )));
  }, [user?.id]);

  const markAllRead = useCallback(async () => {
    if (!user?.id) return;
    await communityService.markAllNotificationsRead(user.id);
    const readAt = new Date().toISOString();
    setNotifications((items) => items.map((item) => ({ ...item, read_at: item.read_at || readAt })));
  }, [user?.id]);

  const createReport = useCallback(async (targetType, targetId, reason, details) => {
    if (!user?.id) throw new Error('Bitte melde dich an, um Inhalte zu melden.');
    return communityService.createReport(user.id, targetType, targetId, reason, details);
  }, [user?.id]);

  const toggleBlock = useCallback(async (blockedId) => {
    if (!user?.id) throw new Error('Bitte melde dich an.');
    const enabled = !blockedIds.has(String(blockedId));
    await communityService.setBlocked(user.id, blockedId, enabled);
    await refreshCommunity();
    return enabled;
  }, [user?.id, blockedIds, refreshCommunity]);

  const getTrust = useCallback(async (userId) => {
    if (!userId) return null;
    if (Object.prototype.hasOwnProperty.call(trustCache, userId)) return trustCache[userId];
    try {
      const trust = await communityService.getTrust(userId);
      setTrustCache((cache) => ({ ...cache, [userId]: trust }));
      return trust;
    } catch (error) {
      console.warn('Vertrauensdaten nicht verfügbar:', error);
      setTrustCache((cache) => ({ ...cache, [userId]: null }));
      return null;
    }
  }, [trustCache]);

  const value = {
    favorites,
    subscriptions,
    searchSubscriptions,
    notifications,
    preferences,
    loading,
    featureAvailable,
    favoriteKeys,
    subscriptionIds,
    blockedIds,
    unreadNotifications: notifications.filter((item) => !item.read_at).length,
    refreshCommunity,
    toggleFavorite,
    toggleProviderSubscription,
    savePreferences,
    createSearchSubscription,
    updateSearchSubscription,
    deleteSearchSubscription,
    markRead,
    markAllRead,
    createReport,
    toggleBlock,
    getTrust,
  };

  return <CommunityContext.Provider value={value}>{children}</CommunityContext.Provider>;
};

export const useCommunity = () => {
  const context = useContext(CommunityContext);
  if (!context) throw new Error('useCommunity muss innerhalb des CommunityProvider verwendet werden.');
  return context;
};
